import { useCallback, useMemo } from "react";
import { graphql, useFragment, useMutation } from "react-relay";

import { displayMutationResults } from "utils/mutations";

import { useCachedCurrentUser } from "./useCurrentUser";

import type { useUserOverrides_setOverrideMutation } from "__generated__/useUserOverrides_setOverrideMutation.graphql";
import type { useUserOverrides_user$key } from "__generated__/useUserOverrides_user.graphql";

export const userOverrideFragment = graphql`
  fragment useUserOverrides_user on User {
    id
    overrides {
      id
      overridesMap
    }
  }
`;

// This mutation is used to set the override value.
export const setOverrideMutationGraphQL = graphql`
  mutation useUserOverrides_setOverrideMutation($scopeId: ID!, $overridesMap: GenericScalar!)
  @raw_response_type
  @team(name: "cloud-infrastructure") {
    setOverrides(scopeId: $scopeId, overridesMap: $overridesMap) {
      errors
      user {
        ...useUserOverrides_user
      }
    }
  }
`;

type UseUserOverrideReturn<OverrideType> = [
  OverrideType, // Current override value (with default)
  (newValue: OverrideType) => void, // Mutation callback to set the override
  boolean, // isMutationInFlight
];

export enum GamepadMode {
  MODE_1 = "MODE 1",
  MODE_2 = "MODE 2",
  MODE_3 = "MODE 3",
  VIDEO_GAME_MODE = "VIDEO GAME MODE",
}

export type GamepadSettings = {
  mode: GamepadMode;
  expoLevel: number;
  throttleSensitivity: number;
  yawSensitivity: number;
  pitchSensitivity: number;
};

type FlightsReportColumnVisibility = {
  linkedDevices: boolean;
  axonEvidence: boolean;
  postFlightReport: boolean;
  group: boolean;
  skydioInternal: boolean;
};

export type CrosshairSettings = {
  enabled: boolean;
  type: "none" | "simple" | "simpleWithAngle" | "simpleWithAngleAndDistance";
};

type VehiclesTableColumnVisibility = {
  software: boolean;
  linked_sensor_package: boolean;
  linked_attachment_top: boolean;
  linked_attachment_bottom: boolean;
  linked_attachment_left: boolean;
  linked_attachment_right: boolean;
  flights: boolean;
  flight_time: boolean;
  licenses: boolean;
  site: boolean;
  linkedDock: boolean;
  linkedExternalRadio: boolean;
  notes: boolean;
  ansi_serial: boolean;
};

export type UserOverrideTypes = {
  click_to_fly_enabled: boolean;
  preview_where_drone_looks: boolean;
  gamepad_settings: GamepadSettings;
  crosshair_settings: CrosshairSettings;
  subject_tracking_mode: "track" | "follow";
  subject_tracking_enabled: boolean;
  autonomous_patrol_mode: "track" | "follow";
  autonomous_patrol_camera_mode: "AUTO" | "EO" | "IR";
  flights_report_column_visibility: FlightsReportColumnVisibility;
  vehicles_table_column_visibility: VehiclesTableColumnVisibility;
};

export const UserOverrideDefaults: UserOverrideTypes = {
  click_to_fly_enabled: false,
  preview_where_drone_looks: false,
  gamepad_settings: {
    mode: GamepadMode.VIDEO_GAME_MODE,
    expoLevel: 0.4,
    throttleSensitivity: 0.8,
    yawSensitivity: 0.2,
    pitchSensitivity: 0.2,
  },
  crosshair_settings: {
    enabled: false,
    type: "simpleWithAngle",
  },
  subject_tracking_mode: "track",
  subject_tracking_enabled: false,
  autonomous_patrol_mode: "track",
  autonomous_patrol_camera_mode: "AUTO",
  flights_report_column_visibility: {
    linkedDevices: false,
    axonEvidence: true,
    postFlightReport: true,
    group: false,
    skydioInternal: false,
  },
  vehicles_table_column_visibility: {
    software: true,
    linked_sensor_package: false,
    linked_attachment_top: false,
    linked_attachment_bottom: false,
    linked_attachment_left: false,
    linked_attachment_right: false,
    flights: false,
    flight_time: false,
    licenses: false,
    site: true,
    linkedDock: true,
    linkedExternalRadio: false,
    notes: true,
    ansi_serial: false,
  },
};

/**
 * Hook to manage a specific user override.
 * @param overrideKey The key for the specific override in the overridesMap.
 * @param defaultValueInitializer Optional function to initialize the default value.  Should only
 * be used for dynamic default values, prefer using the static UserOverrideDefaults.
 * @returns A tuple: [currentValue, setValueCallback, isSettingValue].
 */
export function useUserOverride<
  T extends keyof UserOverrideTypes,
  OverrideType = UserOverrideTypes[T],
>(
  overrideKey: T,
  defaultValueInitializer?: () => OverrideType
): UseUserOverrideReturn<OverrideType> {
  const defaultValue = defaultValueInitializer
    ? defaultValueInitializer()
    : UserOverrideDefaults[overrideKey];
  const currentUser = useCachedCurrentUser();
  const userData = useFragment<useUserOverrides_user$key>(userOverrideFragment, currentUser);

  const [commitMutation, isMutationInFlight] = useMutation<useUserOverrides_setOverrideMutation>(
    setOverrideMutationGraphQL
  );

  const overridesMap = useMemo(() => {
    return userData?.overrides?.overridesMap ? JSON.parse(userData.overrides.overridesMap) : {};
  }, [userData]);
  const currentRawValue = overridesMap[overrideKey];
  let currentValue: OverrideType = defaultValue as OverrideType;

  if (currentRawValue !== undefined) {
    currentValue = currentRawValue as OverrideType;
  }

  const setOverrideValue = useCallback(
    (newValue: OverrideType) => {
      if (!userData?.id) {
        return;
      }
      commitMutation({
        variables: {
          scopeId: userData.id,
          overridesMap: {
            [overrideKey]: newValue,
          },
        },
        optimisticResponse: {
          setOverrides: {
            errors: null,
            user: {
              id: userData.id,
              overrides: {
                id: userData.overrides?.id || "",
                overridesMap: JSON.stringify({
                  ...overridesMap,
                  [overrideKey]: newValue,
                }),
              },
            },
          },
        },
        onCompleted: displayMutationResults(response => response.setOverrides, {}),
      });
    },
    [userData, overrideKey, commitMutation, overridesMap]
  );

  return [currentValue, setOverrideValue, isMutationInFlight];
}
